import { createSlice } from "@reduxjs/toolkit";

import { RootState } from "@/redux";

import {
	CopywritingSettings,
	SendDelayEntry,
} from "../../pages/Company/schema/copywritingSettings";
import { copywritingSettingsApi } from "./copywritingSettings.api";

interface CopywritingSettingsState {
	settings: CopywritingSettings | null;
	/** The delays a newly added campaign step starts with (PREC-4299); null when none are set. */
	sendDelayDefaults: SendDelayEntry[] | null;
	/** Whether a new email draft starts with Include signature on (PREC-4401); null until loaded. */
	appendEmailSignature: boolean | null;
}

const initialState: CopywritingSettingsState = {
	settings: null,
	sendDelayDefaults: null,
	appendEmailSignature: null,
};

const storeSavedSettings = (state: CopywritingSettingsState, settings: CopywritingSettings) => {
	state.settings = settings;
	state.sendDelayDefaults = settings.sendDelayDefaults;
	state.appendEmailSignature = settings.appendEmailSignature ?? state.appendEmailSignature;
};

export const copywritingSettingsSlice = createSlice({
	name: "copywritingSettings",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder.addMatcher(
			copywritingSettingsApi.endpoints.getCopywritingSettings.matchFulfilled,
			(state, { payload }) => {
				state.settings = payload;
			}
		);

		builder.addMatcher(
			copywritingSettingsApi.endpoints.getSendDelayDefaults.matchFulfilled,
			(state, { payload }) => {
				state.sendDelayDefaults = payload.sendDelayDefaults;
			}
		);

		builder.addMatcher(
			copywritingSettingsApi.endpoints.getEmailSignatureDefault.matchFulfilled,
			(state, { payload }) => {
				state.appendEmailSignature = payload.appendEmailSignature;
			}
		);

		builder.addMatcher(
			copywritingSettingsApi.endpoints.upsertCopywritingSettings.matchFulfilled,
			(state, { payload }) => storeSavedSettings(state, payload)
		);

		builder.addMatcher(
			copywritingSettingsApi.endpoints.patchCopywritingSettings.matchFulfilled,
			(state, { payload }) => storeSavedSettings(state, payload)
		);
	},
});

export const copywritingSettingsReducer = copywritingSettingsSlice.reducer;
export const copywritingSettingsSelector = (state: RootState) =>
	state[copywritingSettingsSlice.name];
export const sendDelayDefaultsSelector = (state: RootState) =>
	state[copywritingSettingsSlice.name].sendDelayDefaults;
/** The composer's Include signature default; off until the company's setting has loaded. */
export const emailSignatureDefaultSelector = (state: RootState): boolean =>
	state[copywritingSettingsSlice.name].appendEmailSignature ?? false;
